"use client"

import { useState, useEffect } from "react"
import styled from "styled-components"
import { Globe, TrendingUp, Search, Hash, Heart, MessageSquare, Users, Filter, Sparkles, Star, UserCheck, MapPin, Calendar } from "lucide-react"
import PostCard from "./PostCard"
import PeopleYouMayKnow from "../components/PeopleYouMayKnow"
import { useAuth } from "../context/AuthContext"
import { useTheme } from "../context/ThemeContext"
import { useNavigate } from "react-router-dom"

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
`

const WorldHeader = styled.div`
  background-color: ${props => props.theme.cardBackground};
  border-radius: 8px;
  box-shadow: ${props => props.theme.shadow};
  padding: 20px;
`

const HeaderTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`

const Title = styled.h1`
  font-size: 24px;
  font-weight: 600;
  margin: 0;
  color: ${props => props.theme.textPrimary};
`

const Subtitle = styled.p`
  font-size: 14px;
  color: ${props => props.theme.textSecondary};
  margin: 0 0 20px 0;
`

const FilterTabs = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
`

const FilterTab = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 20px;
  background-color: ${props => props.$active ? props.theme.buttonPrimary : props.theme.cardBackground};
  color: ${props => props.$active ? 'white' : props.theme.textSecondary};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    border-color: ${props => props.theme.buttonPrimary};
    color: ${props => props.$active ? 'white' : props.theme.buttonPrimary};
  }
`

const SearchBar = styled.div`
  position: relative;
  margin-bottom: 16px;
`

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 16px 12px 48px;
  border: 2px solid ${props => props.theme.border};
  border-radius: 12px;
  font-size: 14px;
  outline: none;
  background-color: ${props => props.theme.inputBackground};
  color: ${props => props.theme.textPrimary};
  transition: border-color 0.2s;
  
  &:focus {
    border-color: ${props => props.theme.buttonPrimary};
  }
  
  &::placeholder {
    color: ${props => props.theme.textMuted};
  }
`

const SearchIcon = styled.div`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: ${props => props.theme.textMuted};
`

const StatsSection = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 12px;
  margin-top: 16px;
`

const StatCard = styled.div`
  background-color: ${props => props.theme.hoverBackground};
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px;
  padding: 12px;
  text-align: center;
`

const StatValue = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: ${props => props.theme.textPrimary};
`

const StatLabel = styled.div`
  font-size: 12px;
  color: ${props => props.theme.textSecondary};
  margin-top: 4px;
`

const TrendingSection = styled.div`
  background-color: ${props => props.theme.cardBackground};
  border-radius: 8px;
  box-shadow: ${props => props.theme.shadow};
  padding: 20px;
`

const SectionTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${props => props.theme.textPrimary};
`

const TrendingList = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`

const TrendingTag = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  background-color: ${props => props.theme.buttonPrimary};
  color: white;
  border: none;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: ${props => props.theme.buttonPrimaryHover};
    transform: translateY(-1px);
  }
`

const PostsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const PostsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 4px;
`

const PostsTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${props => props.theme.textPrimary};
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`

const LoadMoreButton = styled.button`
  padding: 12px 24px;
  background-color: ${props => props.theme.buttonPrimary};
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: ${props => props.theme.buttonPrimaryHover};
  }
  
  &:disabled {
    background-color: ${props => props.theme.textMuted};
    cursor: not-allowed;
  }
`

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: ${props => props.theme.textSecondary};
`

const LoadingState = styled.div`
  text-align: center;
  padding: 20px;
  color: ${props => props.theme.textSecondary};
`

const UserSearchResults = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background-color: ${props => props.theme.cardBackground};
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px;
  box-shadow: ${props => props.theme.shadowHover};
  max-height: 300px;
  overflow-y: auto;
  z-index: 10;
  
  .search-result-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    cursor: pointer;
    transition: background-color 0.2s;
    
    &:hover {
      background-color: ${props => props.theme.hoverBackground};
    }
    
    &:not(:last-child) {
      border-bottom: 1px solid ${props => props.theme.border};
    }
  }
`

const UserAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: ${props => props.theme.border};
  background-image: ${props => props.$avatar ? `url(${props.$avatar})` : 'none'};
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.textMuted};
  overflow: hidden;
`

export default function WorldPage() {
  const { isLoggedIn } = useAuth()
  const { theme } = useTheme()
  const navigate = useNavigate()
  const [activeFilter, setActiveFilter] = useState("recent")
  const [searchQuery, setSearchQuery] = useState("")
  const [posts, setPosts] = useState([])
  const [trendingTags, setTrendingTags] = useState([])
  const [stats, setStats] = useState({ totalPosts: 0, totalUsers: 0, totalLikes: 0 })
  const [loading, setLoading] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [page, setPage] = useState(1)
  const [recommendedUsers, setRecommendedUsers] = useState([])
  const [userSearchResults, setUserSearchResults] = useState([])

  // 초기 데이터 로드
  useEffect(() => {
    fetchInitialData()
    fetchTrendingTags()
    fetchStats()
    fetchRecommendedUsers()
  }, [])

  // 필터 변경 시 게시물 다시 로드
  useEffect(() => {
    fetchPosts(true)
  }, [activeFilter, searchQuery])

  const fetchInitialData = async () => {
    await fetchPosts(true)
  }

  const fetchPosts = async (reset = false) => {
    setLoading(true)
    try {
      const currentPage = reset ? 1 : page
      const endpoint = getPostsEndpoint(currentPage)
      
      const response = await fetch(endpoint, {
        credentials: 'include'
      })
      
      const data = await response.json()
      if (data.success) {
        if (reset) {
          setPosts(data.posts)
          setPage(2)
        } else {
          setPosts(prev => [...prev, ...data.posts])
          setPage(prev => prev + 1)
        }
        
        setHasMore(data.posts.length === 20) // 페이지당 20개씩 로드
      }
    } catch (error) {
      console.error('게시물 로드 오류:', error)
    } finally {
      setLoading(false)
    }
  }

  const getPostsEndpoint = (pageNum) => {
    let endpoint = '/post/list'
    const params = new URLSearchParams({
      page: pageNum,
      limit: 20
    })

    if (searchQuery) {
      endpoint = '/search/posts'
      params.append('q', searchQuery)
    } else {
      switch (activeFilter) {
        case 'trending':
          endpoint = '/post/recommended-posts/more'
          break
        case 'popular':
          params.append('sort', 'likes')
          break
        default: // recent
          params.append('sort', 'recent')
          break
      }
    }

    return `${endpoint}?${params.toString()}`
  }

  const fetchTrendingTags = async () => {
    try {
      const response = await fetch('/post/popular-tags?limit=10', {
        credentials: 'include'
      })
      
      const data = await response.json()
      if (data.success) {
        setTrendingTags(data.tags)
      }
    } catch (error) {
      console.error('인기 태그 로드 오류:', error)
    }
  }

  const fetchStats = async () => {
    try {
      console.log('월드 통계 API 호출 시작')
      
      const response = await fetch('/api/world-stats', {
        credentials: 'include'
      })
      
      console.log('월드 통계 API 응답 상태:', response.status)
      
      if (response.ok) {
        const data = await response.json()
        console.log('월드 통계 API 응답 데이터:', data)
        
        if (data.success && data.stats) {
          setStats({
            totalPosts: data.stats.totalPosts || 0,
            totalUsers: data.stats.totalUsers || 0,
            totalLikes: data.stats.totalLikes || 0
          })
          console.log('월드 통계 상태 업데이트 완료:', data.stats)
        } else {
          console.error('월드 통계 API 성공하지만 데이터 없음:', data)
          setStats({ totalPosts: 0, totalUsers: 0, totalLikes: 0 })
        }
      } else {
        const errorText = await response.text()
        console.error('월드 통계 API 호출 실패:', response.status, errorText)
        setStats({ totalPosts: 0, totalUsers: 0, totalLikes: 0 })
      }
    } catch (error) {
      console.error('통계 로드 오류:', error)
      console.error('오류 스택:', error.stack)
      // 기본값 설정
      setStats({ totalPosts: 0, totalUsers: 0, totalLikes: 0 })
    }
  }

  const handleTagClick = (tagName) => {
    setSearchQuery(`#${tagName}`)
  }

  const handleSearchChange = (e) => {
    const value = e.target.value
    setSearchQuery(value)
    handleUserSearch(value)
  }

  const handleLoadMore = () => {
    fetchPosts(false)
  }

  const fetchRecommendedUsers = async () => {
    try {
      const response = await fetch('/user/recommended?limit=5', {
        credentials: 'include'
      })
      
      if (response.ok) {
        const data = await response.json()
        if (data.success) {
          setRecommendedUsers(data.users)
        }
      }
    } catch (error) {
      console.error('추천 사용자 로드 오류:', error)
    }
  }

  const searchUsers = async (query) => {
    if (!query.trim()) {
      setUserSearchResults([])
      return
    }
    
    try {
      const response = await fetch(`/search/users?q=${encodeURIComponent(query)}&limit=10`, {
        credentials: 'include'
      })
      
      if (response.ok) {
        const data = await response.json()
        if (data.success) {
          setUserSearchResults(data.users)
        }
      }
    } catch (error) {
      console.error('사용자 검색 오류:', error)
    }
  }

  const handleUserSearch = (query) => {
    if (query.startsWith('@')) {
      searchUsers(query.slice(1))
    } else {
      // 일반 검색은 기존 로직 유지
      setUserSearchResults([])
    }
  }

  return (
    <Container>
      {/* 월드 헤더 */}
      <WorldHeader theme={theme}>
        <HeaderTitle>
          <Globe size={28} color="#2563eb" />
          <Title theme={theme}>월드</Title>
        </HeaderTitle>
        <Subtitle theme={theme}>모든 사용자들과 소통하고 다양한 이야기를 발견해보세요</Subtitle>
        
        {/* 검색바 */}
        <SearchBar>
          <SearchIcon theme={theme}>
            <Search size={20} />
          </SearchIcon>
          <SearchInput
            theme={theme}
            type="text"
            placeholder="게시물, 해시태그 검색... (@username으로 사용자 검색)"
            value={searchQuery}
            onChange={handleSearchChange}
          />
        </SearchBar>
        
        {/* 필터 탭 */}
        <FilterTabs>
          <FilterTab 
            theme={theme}
            $active={activeFilter === 'recent'} 
            onClick={() => setActiveFilter('recent')}
          >
            <Sparkles size={16} />
            최신
          </FilterTab>
          <FilterTab 
            theme={theme}
            $active={activeFilter === 'trending'} 
            onClick={() => setActiveFilter('trending')}
          >
            <TrendingUp size={16} />
            인기
          </FilterTab>
          <FilterTab 
            theme={theme}
            $active={activeFilter === 'popular'} 
            onClick={() => setActiveFilter('popular')}
          >
            <Star size={16} />
            좋아요순
          </FilterTab>
        </FilterTabs>

        {/* 통계 */}
        <StatsSection>
          <StatCard theme={theme}>
            <StatValue theme={theme}>{stats.totalPosts.toLocaleString()}</StatValue>
            <StatLabel theme={theme}>총 게시물</StatLabel>
          </StatCard>
          <StatCard theme={theme}>
            <StatValue theme={theme}>{stats.totalUsers.toLocaleString()}</StatValue>
            <StatLabel theme={theme}>활성 사용자</StatLabel>
          </StatCard>
          <StatCard theme={theme}>
            <StatValue theme={theme}>{stats.totalLikes.toLocaleString()}</StatValue>
            <StatLabel theme={theme}>총 좋아요</StatLabel>
          </StatCard>
        </StatsSection>
      </WorldHeader>

      {/* 사용자 검색 결과 */}
      {userSearchResults.length > 0 && (
        <div style={{ position: 'relative' }}>
          <UserSearchResults theme={theme}>
            {userSearchResults.map((user) => (
              <div 
                key={user.id} 
                className="search-result-item"
                onClick={() => {
                  navigate(`/profile/${user.username}`)
                  setSearchQuery('')
                  setUserSearchResults([])
                }}
              >
                <UserAvatar theme={theme} $avatar={user.avatar_url}>
                  {!user.avatar_url && <Users size={20} />}
                </UserAvatar>
                <div>
                  <div style={{ fontWeight: '600', color: theme.textPrimary }}>{user.full_name || user.username}</div>
                  <div style={{ fontSize: '14px', color: theme.textSecondary }}>@{user.username}</div>
                  {user.bio && (
                    <div style={{ fontSize: '12px', color: theme.textMuted, marginTop: '2px' }}>
                      {user.bio.length > 50 ? `${user.bio.substring(0, 50)}...` : user.bio}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </UserSearchResults>
        </div>
      )}

      {/* 인기 해시태그 */}
      {trendingTags.length > 0 && !searchQuery && (
        <TrendingSection theme={theme}>
          <SectionTitle theme={theme}>
            <Hash size={20} />
            인기 해시태그
          </SectionTitle>
          <TrendingList>
            {trendingTags.map((tag, index) => (
              <TrendingTag key={index} theme={theme} onClick={() => handleTagClick(tag.tag_name)}>
                <Hash size={12} />
                {tag.tag_name}
                <span style={{ fontSize: '10px', opacity: 0.8, color: theme.textSecondary }}>({tag.usage_count})</span>
              </TrendingTag>
            ))}
          </TrendingList>
        </TrendingSection>
      )}


      {/* 게시물 섹션 */}
      <PostsSection>
        <PostsHeader>
          <PostsTitle theme={theme}>
            <Globe size={20} color={theme.textPrimary} />
            {searchQuery ? `"${searchQuery}" 검색 결과` : 
             activeFilter === 'recent' ? '최신 게시물' : 
             activeFilter === 'trending' ? '인기 게시물' : '좋아요 많은 게시물'}
          </PostsTitle>
        </PostsHeader>

        {loading && posts.length === 0 ? (
          <LoadingState theme={theme}>게시물을 불러오는 중...</LoadingState>
        ) : posts.length === 0 ? (
          <EmptyState theme={theme}>
            <p>{searchQuery ? `"${searchQuery}"에 대한 게시물이 없습니다.` : '게시물이 없습니다.'}</p>
            <p>첫 번째 게시물을 작성해보세요!</p>
          </EmptyState>
        ) : (
          <>
            {posts.map((post) => (
              <PostCard
                key={post.id}
                post={{
                  id: post.id,
                  author_username: post.author_username,
                  author_full_name: post.author_full_name,
                  author_avatar: post.author_avatar,
                  created_at: post.created_at,
                  images: post.images,
                  tags: post.tags,
                  content: post.content,
                  like_count: post.like_count,
                  comment_count: post.comment_count
                }}
                onPostDeleted={(deletedId) => {
                  setPosts(prev => prev.filter(p => p.id !== deletedId))
                }}
                onPostUpdated={() => {
                  fetchPosts(true) // 게시물 수정 후 전체 목록 새로고침
                }}
              />
            ))}

            {hasMore && (
              <div style={{ textAlign: 'center', marginTop: '20px' }}>
                <LoadMoreButton theme={theme} onClick={handleLoadMore} disabled={loading}>
                  {loading ? '로딩 중...' : '더 보기'}
                </LoadMoreButton>
              </div>
            )}
          </>
        )}
      </PostsSection>
    </Container>
  )
}